import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import {
  DEFAULT_PAGE_SIZE,
  viewTypes,
} from "@redotech/redo-views-common/views";
import { SHORTAGE_COLUMN_KEY } from "../inventory-shortage";
import { RedoListingStatus } from "../redo-listing-status";

// Browse views default to hiding archived listings. Seeded as a default filter
// so the user can override it (edit/clear the Status filter) and their choice
// wins.
const hideArchivedFilter: AdvancedFilterData = {
  name: "status",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [RedoListingStatus.ARCHIVED],
  operator: ArrayStringFilterOperator.NONE_OF,
};

export enum InventoryLevelsSystemView {
  ALL = "all",
  FORECASTING = "forecasting",
  SHORTAGES = "shortages",
}

export const sortDefault: TableSort = {
  direction: SortDirection.ASC,
  key: "title",
};

const defaultColumns = [
  { key: "selected", width: 40, hidden: false },
  { key: "title", width: 350, hidden: false },
  { key: "locations", width: 150, hidden: false },
  { key: "vendor", width: 150, hidden: false },
  { key: "inventoryValue", width: 150, hidden: false },
  { key: "merchant", width: 180, hidden: true },
  { key: "totalAvailable", width: 120, hidden: false },
  { key: "totalQuantity", width: 120, hidden: false },
  { key: "totalCommitted", width: 120, hidden: false },
  { key: "incomingShipments", width: 150, hidden: false },
];

const forecastingColumns = [
  { key: "selected", width: 40, hidden: false },
  { key: "title", width: 300, hidden: false, pinned: true },
  { key: "locations", width: 150, hidden: false },
  { key: "forecastingEnabled", width: 130, hidden: false },
  { key: "daysOfStockRemaining", width: 150, hidden: false },
  { key: "daysUntilStockout", width: 150, hidden: true },
  { key: "stockoutDate", width: 150, hidden: true },
  { key: "reorderThreshold", width: 160, hidden: false },
  { key: "daysUntilReorder", width: 150, hidden: false },
  { key: "unitsToOrder", width: 150, hidden: false },
  { key: "totalQuantity", width: 150, hidden: false },
  { key: "incomingShipments", width: 150, hidden: false },
  { key: "moq", width: 100, hidden: true },
  { key: "leadTime", width: 150, hidden: false },
  { key: "buffer", width: 150, hidden: false },
  { key: "inventoryValue", width: 150, hidden: false },
  { key: "supplier", width: 150, hidden: false },
  { key: "vendor", width: 150, hidden: false },
  { key: "sold30Days", width: 130, hidden: false },
  { key: "sold60Days", width: 130, hidden: false },
  { key: "sold90Days", width: 130, hidden: false },
  { key: "sold180Days", width: 130, hidden: true },
  { key: "sold365Days", width: 130, hidden: true },
  { key: "sold7Days", width: 130, hidden: true },
  { key: "sold14Days", width: 130, hidden: true },
  { key: "sold21Days", width: 130, hidden: true },
  { key: "sold45Days", width: 130, hidden: true },
  { key: "sold120Days", width: 130, hidden: true },
  { key: "forecast30Days", width: 150, hidden: false },
  { key: "forecast60Days", width: 150, hidden: false },
  { key: "forecast90Days", width: 150, hidden: false },
  { key: "forecast180Days", width: 150, hidden: true },
  { key: "forecast365Days", width: 150, hidden: true },
  { key: "forecast7Days", width: 150, hidden: true },
  { key: "forecast14Days", width: 150, hidden: true },
  { key: "forecast21Days", width: 150, hidden: true },
  { key: "forecast45Days", width: 150, hidden: true },
  { key: "forecast120Days", width: 150, hidden: true },
  { key: "totalAvailable", width: 120, hidden: true },
  { key: "totalCommitted", width: 120, hidden: true },
];

const shortagesColumns = [
  { key: "selected", width: 40, hidden: false },
  { key: "title", width: 350, hidden: false },
  { key: "locations", width: 150, hidden: false },
  { key: SHORTAGE_COLUMN_KEY, width: 120, hidden: false },
  { key: "totalBuildable", width: 120, hidden: false },
  { key: "totalAvailable", width: 120, hidden: false },
  { key: "totalCommitted", width: 120, hidden: false },
  { key: "totalQuantity", width: 120, hidden: false },
  { key: "totalAvailableWithBuildable", width: 140, hidden: true },
  { key: "vendor", width: 150, hidden: true },
  { key: "merchant", width: 180, hidden: true },
  { key: "inventoryValue", width: 150, hidden: true },
  { key: "incomingShipments", width: 150, hidden: true },
];

const shortageSort: TableSort = {
  direction: SortDirection.DESC,
  key: SHORTAGE_COLUMN_KEY,
};

export const StaticSystemViews: Record<
  InventoryLevelsSystemView,
  viewTypes["inventory-levels"]
> = {
  [InventoryLevelsSystemView.ALL]: {
    table: "inventory-levels",
    name: InventoryLevelsSystemView.ALL,
    filters: [hideArchivedFilter],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
    isNotEditable: true,
  },
  [InventoryLevelsSystemView.FORECASTING]: {
    table: "inventory-levels",
    name: InventoryLevelsSystemView.FORECASTING,
    filters: [
      {
        name: "forecastingEnabled",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      hideArchivedFilter,
    ],
    columns: forecastingColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 1,
    isNotEditable: true,
  },
  [InventoryLevelsSystemView.SHORTAGES]: {
    table: "inventory-levels",
    name: InventoryLevelsSystemView.SHORTAGES,
    filters: [
      {
        name: "hasShortage",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      hideArchivedFilter,
    ],
    columns: shortagesColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shortageSort,
    position: 2,
    isNotEditable: true,
  },
};

export function friendlyViewName(view: viewTypes["inventory-levels"]) {
  switch (view.name) {
    case InventoryLevelsSystemView.ALL:
      return "All";
    case InventoryLevelsSystemView.FORECASTING:
      return "Forecasting";
    case InventoryLevelsSystemView.SHORTAGES:
      return "Shortages";
    default:
      return view.name;
  }
}
